import clsx from "clsx";

interface Step<T> {
  key: T;
  label: string;
}

export interface StepperProps<T> {
  currentStep: T;
  steps: Step<T>[];
  containerClassNames?: string;
}

export function Stepper<T extends number>({ steps, currentStep, containerClassNames }: StepperProps<T>) {
  const isStepCompleted = (stepKey: T) => steps.map(({ key }) => key).indexOf(stepKey) < currentStep;
  const isActiveStep = (stepKey: T) => steps.map(({ key }) => key).indexOf(stepKey) === currentStep;

  return (
    <div
      className={clsx(
        "w-full",
        "min-h-[46px]",
        "border-y",
        "border-background-surface-neutral-subtle",
        "flex",
        "flex-row",
        "flex-no-wrap",
        "overflow-x-auto",
        "overflow-y-hidden",
        "justify-center",
        "items-center",
        "gap-x-5",
        containerClassNames
      )}
      data-testid="stepper"
    >
      {steps.map(({ key, label }, index) => (
        <div key={`stepper-header-${key}`} className="flex flex-row items-center gap-x-5">
          <div className="flex py-3">
            {isStepCompleted(key) ? (
              <div
                className={clsx(
                  "rounded-sm",
                  "w-5",
                  "h-5",
                  "bg-positive-500",
                  "flex",
                  "items-center",
                  "justify-center"
                )}
                data-testid="stepper-check-icon"
              >
                <i className="text-white icon-lt-check-line" />
              </div>
            ) : (
              <div
                className={clsx(
                  "border-2",
                  "rounded-sm",
                  "w-5",
                  "h-5",
                  isActiveStep(key) ? "border-border-action" : "border-border-action"
                )}
                data-testid="stepper-step-number"
              >
                <span
                  className={clsx(
                    "pt-0.5",
                    "flex",
                    "justify-center",
                    "text-xs",
                    "leading-3",
                    "font-medium",
                    isActiveStep(key) ? "text-border-action-hover" : "text-text-base-tertiary"
                  )}
                >
                  {index + 1}
                </span>
              </div>
            )}

            <span
              className={clsx(
                "text-sm",
                "ml-2.5",
                "pt-0.5",
                "leading-4",
                "font-medium",
                "whitespace-nowrap",
                isActiveStep(key) ? "text-text-base-primary" : "text-text-base-tertiary"
              )}
              data-testid="stepper-label"
            >
              {label}
            </span>
          </div>

          {index < steps.length - 1 && (
            <div data-testid="stepper-divider" key={`divider-${label}`} className="flex">
              <div className="justify-center mt-0 border-t w-9 border-border-action" />
            </div>
          )}
        </div>
      ))}
    </div>
  );
}
